// Ficha 06 · Cotización: enganche, semanas y cuota semanal ligados, con matemática local en vivo.
// La primera llamada a `quote` origina el crédito; las siguientes re-cotizan y sus números mandan.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button, Loading, NumberInput } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { quoteWeeklyFromDownPayment } from '@/shared/domain/quote';
import { ApiError } from '@/api/errors';
import { useContractPreview, useQuoteCredit, useWizardSession } from '../api/useWizard';
import { useLatestOnlyQuote } from '../api/useLatestOnlyQuote';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { wizardErrorCode, wizardErrorMsg } from '../api/wizardError';
import {
    noFeasibleDownPaymentMessage,
    productNotFinanceableFailure,
    quoteFailure,
    type QuoteFailure,
} from '../quoteErrors';
import { isAnchorUnfinanceable } from '../buildSale';
import {
    downPaymentRange,
    learnDownPaymentPcts,
    noFeasibleDownPayment,
    pctsFromEffectiveTerms,
    recoveryDownPaymentRange,
    UNKNOWN_DOWN_PAYMENT_PCTS,
    type DownPaymentPcts,
} from '../downPaymentBounds';
import { promoSavings } from '../promoSavings';
import { LevelChip } from '../components/LevelChip';
import { CreditBreakdown } from '../components/CreditBreakdown';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { previousStepSlug } from '../steps';
import { ChargeBreakdown } from '../components/ChargeBreakdown';
import { QuoteSaleTotals, type QuoteTableSlots } from '../components/QuoteSaleTotals';
import { QuoteSplitSummary, type QuoteSplitNumbers } from '../components/QuoteSplitSummary';
import { lineDiscountsOf, quoteTableOf } from '../quoteBreakdown';
import { chargeLinesOf, financingLineOf, linesTotalCents, serverSplitOf } from '../saleCharge';
import { quoteHeadlineOf, quotePriceCentsOf } from '../quoteHeadline';
import type { CreditSummary, QuotePlan, QuoteResult } from '../types';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { LinkedQuoteCards } from '../components/LinkedQuoteCards';
import {
    applyLinkedChange,
    fixLinkedField,
    INITIAL_FIXED,
    linkedTermsContext,
    nearestTerm,
    paymentRange,
    type FixedField,
    type LinkedQuoteChange,
    type LinkedTermsContext,
} from '../linkedQuoteTerms';

/** Enganche mínimo del editor de rescate cuando no se conoce el del plan, en pesos. */
const STEP = 50;

/** Paso del slider del editor de rescate en pesos; el input acepta centavos. */
const SLIDER_STEP = 1;

/** El enganche vive en pesos con centavos: `166725` → `1667.25`. */
const centsToPesos = (cents: number): number => cents / 100;

/** Centavos exactos de un monto en pesos, sin arrastrar el error de coma flotante. */
const pesosToCents = (pesos: number): number => Math.round(pesos * 100);

/** Espera tras el último movimiento del enganche antes de re-cotizar. */
const QUOTE_DEBOUNCE_MS = 500;

/** Lugares para las piezas de Promociones en la Cotización; sin pieza no dejan hueco. */
export type QuoteSlots = QuoteTableSlots;

export function QuoteStep({ sessionId, slots = {} }: { sessionId: string; slots?: QuoteSlots }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const quote = useQuoteCredit(sessionId);
    // Una sola cotización en vuelo: dos en paralelo chocan en el candado optimista de loan-credit.
    const latestQuote = useLatestOnlyQuote(quote.mutateAsync);
    const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [plan, setPlan] = useState<QuotePlan | null>(null);
    const [server, setServer] = useState<CreditSummary | null>(null);
    // Última cotización confirmada, legible desde callbacks que se crearon en renders anteriores.
    const serverRef = useRef<CreditSummary | null>(null);
    // Cotas EFECTIVAS que la pantalla fue conociendo: el plan no las trae y una política de la
    // tienda las aprieta. Viven en estado para que el control se redibuje con ellas.
    const [effectivePcts, setEffectivePcts] = useState<DownPaymentPcts>(UNKNOWN_DOWN_PAYMENT_PCTS);
    const [down, setDown] = useState<number | null>(null); // pesos con centavos
    const [term, setTerm] = useState<number | null>(null);
    // Fallo del bootstrap: mensaje accionable + la acción que lo resuelve (null = sin error). Un
    // rechazo de negocio NO trae 'retry': reintentarlo es una trampa para el vendedor.
    const [bootFailure, setBootFailure] = useState<QuoteFailure | null>(null);
    // Contador en las deps del efecto: resetear solo el ref no re-dispara nada (los refs no
    // provocan render), así que "Reintentar" incrementa esto para relanzar el bootstrap.
    const [bootRetry, setBootRetry] = useState(0);
    // El valor fijo (enganche o pago semanal); el otro se calcula para el plazo elegido.
    const [fixed, setFixed] = useState<FixedField>(INITIAL_FIXED);
    // Pago semanal que el vendedor fijó; solo cuenta mientras el pago es el fijo.
    const [payTarget, setPayTarget] = useState<number | null>(null);
    const [termsNotice, setTermsNotice] = useState<string | null>(null);
    // Bono de la promo ganadora de la última cotización confirmada.
    const [bonusCents, setBonusCents] = useState<number | undefined>(undefined);
    // Rechazo que se corrige en las tarjetas: va junto a ellas, sin cambiar de vista.
    const [quoteNotice, setQuoteNotice] = useState<string | null>(null);
    // Loading del botón Continuar — local a propósito (no quote.isPending: ver nota del hero).
    const [continuing, setContinuing] = useState(false);
    /** El silo avisó que el cobro ya entró: la sesión que tenía el front estaba vieja. */
    const [lockedByPayment, setLockedByPayment] = useState(false);
    // Loading del «Cotizar de nuevo» del editor de rescate — local, igual que el de Continuar.
    const [requoting, setRequoting] = useState(false);
    const bootStarted = useRef(false);

    // Con reparto del servidor se cotiza lo que financia; sin él, la financiada o el equipo.
    const financedLine = session ? financingLineOf(session) : undefined;
    const headline = session ? quoteHeadlineOf(session) : undefined;
    const priceCents =
        (session ? quotePriceCentsOf(session) : undefined) ??
        financedLine?.basePriceCents ??
        session?.product?.cashPriceCents ??
        0;
    const price = priceCents / 100;
    const level = session?.loan?.level;
    // Sin ancla financiable no hay monto que cotizar: el arranque esperaría un precio que nunca llega.
    const unquotable =
        !!session &&
        (isAnchorUnfinanceable(session) ||
            (serverSplitOf(session) !== undefined && priceCents <= 0));

    // El crédito que el vendedor ya acordó con el cliente. Los bounds del plan solo llegan con el
    // quote, así que el boot igual cotiza: lo que cambia es con QUÉ términos.
    const creditId = session?.loan?.creditId;
    const preview = useContractPreview(sessionId, !!creditId);
    const agreed = preview.data?.credit;
    // Con el enganche cobrado el silo rechaza re-cotizar: el motor recalcularía el enganche bajo un
    // pago hecho. No hay nada que editar, así que el paso se vuelve de solo lectura.
    const locked = !!session?.payment || lockedByPayment;
    // La lectura del crédito NO puede caer a la heurística: el quote ORIGINA o RE-COTIZA, así que
    // un preview caído reescribiría a 20%/12 los términos que el vendedor ya acordó.
    // Exige que NO haya data: React Query conserva la anterior, y un refetch fallido al volver el
    // foco reemplazaba el editor cargado por el aviso de que no se pudo leer nada.
    const previewFailed = !!creditId && preview.isError && agreed === undefined;
    const agreedSettled = !creditId || agreed !== undefined;

    /** Guarda la cotización y, si el silo ya las publica, sus cotas efectivas. */
    const applyQuoteResult = (r: QuoteResult): void => {
        setPlan(r.plan);
        setServer(r.credit);
        serverRef.current = r.credit;
        setBonusCents(r.creditPromotionBonusCents);
        const pcts = pctsFromEffectiveTerms(r.effectiveTerms, priceCents);
        if (pcts !== null) setEffectivePcts(pcts);
    };

    /**
     * Se queda con la cota que el motor reveló al rechazar el enganche, y acomoda el control a ella:
     * dejarlo en un monto que ya sabemos rechazado es volver a ofrecer lo que no se puede cotizar.
     */
    const learnFromRejection = (error: unknown, downPesos?: number): void => {
        const learned = learnDownPaymentPcts(effectivePcts, error, priceCents);
        if (learned === effectivePcts) return;
        setEffectivePcts(learned);
        if (downPesos === undefined) return;
        // Sin plan el rango sale de lo aprendido: el arranque que falla nunca lo resuelve.
        const next =
            plan !== null
                ? downPaymentRange(priceCents, plan, learned)
                : recoveryDownPaymentRange(priceCents, learned);
        setDown(Math.min(Math.max(downPesos, next.min ?? 0), next.max ?? Number.MAX_SAFE_INTEGER));
    };

    // ── Bootstrap: la 1ª cotización resuelve el plan. Sin crédito previo, heurística 20% + plazo 12
    // con auto-corrección; con crédito previo, los términos que ya están acordados.
    useEffect(() => {
        if (bootStarted.current || !session || !level || locked) return;
        if (!unquotable) return;
        bootStarted.current = true;
        toast.error(productNotFinanceableFailure().message);
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'payment-method' },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- una sola vez (guard bootStarted)
    }, [session, level, locked, unquotable]);

    useEffect(() => {
        if (bootStarted.current || !session || priceCents <= 0 || !level) return;
        if (locked || !agreedSettled) return;
        bootStarted.current = true;
        void (async () => {
            let weeks = agreed?.termWeeks ?? 12;
            let downCents = agreed?.downPaymentCents ?? Math.ceil(priceCents * 0.2);
            let lastError: unknown = null;
            // El enganche se sube al mínimo real UNA sola vez: si ese mínimo también se rechaza,
            // el vendedor ve el error en vez de quedar en un ciclo de recotizaciones.
            let raisedToMinimum = false;
            let loweredToMaximum = false;
            for (let intento = 0; intento < 4; intento++) {
                try {
                    const r = await quote.mutateAsync({
                        downPaymentCents: downCents,
                        termWeeks: weeks,
                    });
                    applyQuoteResult(r);
                    setDown(centsToPesos(r.credit.downPaymentCents));
                    setTerm(r.credit.termWeeks);
                    return;
                } catch (e) {
                    lastError = e;
                    learnFromRejection(e);
                    if (e instanceof ApiError) {
                        const d = e.data as {
                            available?: number[];
                            termWeeksOptions?: number[];
                            minCents?: number;
                            maxCents?: number | null;
                            minDownPaymentPct?: number;
                            maxDownPaymentPct?: number;
                        } | null;
                        const terms = d?.termWeeksOptions ?? d?.available;
                        if (e.code === 'TERM_NOT_AVAILABLE' && terms?.length) {
                            weeks = terms[0];
                            continue;
                        }
                        if (e.code === 'DOWN_PAYMENT_OUT_OF_RANGE') {
                            const tooHigh =
                                typeof d?.maxCents === 'number' && downCents > d.maxCents;
                            const next = tooHigh ? d.maxCents : d?.minCents;
                            if (typeof next === 'number' && next !== downCents) {
                                downCents = next;
                                continue;
                            }
                        }
                        // El pct viene decimal sobre 1 (0.4 = 40 %) contra el precio en cents.
                        if (
                            e.code === 'DOWN_PAYMENT_TOO_LOW' &&
                            !raisedToMinimum &&
                            typeof d?.minDownPaymentPct === 'number'
                        ) {
                            raisedToMinimum = true;
                            downCents = Math.ceil(priceCents * d.minDownPaymentPct);
                            continue;
                        }
                        // Un enganche ya acordado puede pasar del máximo vigente: se baja al tope una vez.
                        if (
                            e.code === 'DOWN_PAYMENT_TOO_HIGH' &&
                            !loweredToMaximum &&
                            typeof d?.maxDownPaymentPct === 'number'
                        ) {
                            loweredToMaximum = true;
                            downCents = Math.floor(priceCents * d.maxDownPaymentPct + 1e-6);
                            continue;
                        }
                    }
                    break;
                }
            }
            // El cobro entró entre que el front leyó la sesión y cotizó: el paso pasa a solo lectura
            // en vez de morir en un error sin salida adelante.
            if (wizardErrorCode(lastError) === 'PAYMENT_ALREADY_REGISTERED') {
                setLockedByPayment(true);
                // La sesión que tenía el front es anterior al cobro: sin esto el stepper y la barra
                // del cliente contradicen al aviso que el vendedor está leyendo.
                void refresh();
                return;
            }
            // La reserva del equipo (30 min) la validan todos los endpoints de la venta: si venció,
            // aquí no hay nada que reintentar — la recuperación es re-reservar en el paso 3.
            if (wizardErrorCode(lastError) === 'RESERVATION_EXPIRED') {
                toast.error('Falta la reserva del equipo — vuelve a elegirlo para continuar.');
                await navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: 'product' },
                });
                return;
            }
            // Deja en los controles los términos del último intento: es lo que hay que mover.
            setDown(centsToPesos(downCents));
            setTerm(weeks);
            setBootFailure(quoteFailure(lastError, priceCents));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap de una sola vez (guard bootStarted)
    }, [session, priceCents, level, locked, agreedSettled, bootRetry]);

    // ── Bounds en pesos. Manda el mínimo EFECTIVO que la pantalla conoce, no el del plan: con una
    // política de tienda que sube el piso, la barra del plan ofrecía montos que el motor rechaza.
    const range = plan ? downPaymentRange(priceCents, plan, effectivePcts) : null;
    const minDown = range?.min ?? 0;
    const maxDown = range?.max ?? 0;
    /** Cotas para el editor de rescate, donde no hay plan y solo se conoce lo que reveló el rechazo. */
    const recoveryRange = recoveryDownPaymentRange(priceCents, effectivePcts);
    // Las dos cotas se contradicen: el piso que impone el techo de monto pasa del techo de enganche.
    const sinSalida = bootFailure !== null && noFeasibleDownPayment(effectivePcts);
    const capFinanciable =
        effectivePcts.minPct === null
            ? undefined
            : Math.round(priceCents * (1 - effectivePcts.minPct));

    const clampDown = (v: number): number =>
        Math.min(Math.max(centsToPesos(pesosToCents(v)), minDown), maxDown);

    /** Clamp del editor de rescate: solo aplica los extremos que la pantalla llegó a conocer. */
    const clampToRecoveryRange = (v: number): number => {
        const withFloor = recoveryRange.min === null ? v : Math.max(v, recoveryRange.min);
        return recoveryRange.max === null ? withFloor : Math.min(withFloor, recoveryRange.max);
    };

    // ── Matemática local instantánea, con la misma TNA que el solver de las tarjetas.
    const localTna = server?.tnaAnnual ?? plan?.tnaAnnual ?? null;
    const local = useMemo(() => {
        if (!plan || down === null || term === null || localTna === null) return null;
        return quoteWeeklyFromDownPayment({
            price,
            downPayment: down,
            weeks: term,
            tna: localTna,
            openingFeePct: plan.openingCommissionPct,
        });
    }, [plan, down, term, price, localTna]);

    // Números mostrados: el server manda cuando su re-cotización coincide con los términos vigentes.
    const serverFresh =
        server !== null &&
        down !== null &&
        term !== null &&
        server.downPaymentCents === pesosToCents(down) &&
        server.termWeeks === term;
    // La TNA viaja con los montos: una promoción o una política pueden pisar la del plan, y el
    // desglose tiene que declarar la tasa con la que de verdad se calcularon esos intereses.
    const shown = serverFresh
        ? {
              weekly: server.weeklyPaymentWithIvaCents / 100,
              financed: server.financedCapitalCents / 100,
              interest: server.totalInterestCents / 100,
              iva: server.totalIvaCents / 100,
              tna: server.tnaAnnual,
          }
        : local?.valid && localTna !== null
          ? {
                weekly: local.weeks[0].payment,
                financed: local.financed,
                interest: local.totals.interest,
                iva: local.totals.vat,
                tna: localTna,
            }
          : null;
    const totalCliente =
        shown && down !== null ? down + shown.financed + shown.interest + shown.iva : null;

    // Lo que la promoción le ahorra al cliente: la tasa del PLAN contra la que el motor cobró.
    // `null` cuando no hay promoción de tasa — entonces no se tacha nada.
    const savings =
        plan && shown && down !== null && term !== null
            ? promoSavings({
                  price,
                  downPayment: down,
                  weeks: term,
                  baseTna: plan.tnaAnnual,
                  appliedTna: shown.tna,
                  openingFeePct: plan.openingCommissionPct,
              })
            : null;

    // El enganche mínimo del plan, cuando la promoción lo bajó. Una cota efectiva MAYOR es una
    // política de la tienda apretando, no una promoción: esa no se anuncia como mejora.
    const promoLoweredMinDown =
        plan !== null &&
        effectivePcts.minPct !== null &&
        effectivePcts.minPct < plan.minDownPaymentPct
            ? price * plan.minDownPaymentPct
            : null;

    useEffect(
        () => () => {
            if (commitTimer.current !== null) clearTimeout(commitTimer.current);
        },
        [],
    );

    const cancelScheduledCommit = (): void => {
        if (commitTimer.current === null) return;
        clearTimeout(commitTimer.current);
        commitTimer.current = null;
    };

    /** Pone en pantalla los términos de un crédito confirmado: los números no mezclan respuestas. */
    const showCredit = (c: CreditSummary): void => {
        setDown(centsToPesos(c.downPaymentCents));
        setTerm(c.termWeeks);
    };

    /** Tras un fallo: vuelve a la última cotización buena y luego a lo que quedó guardado. */
    const resyncFromSaved = async (): Promise<void> => {
        const ticket = latestQuote.currentTicket();
        if (serverRef.current !== null) showCredit(serverRef.current);
        void refresh();
        let saved: CreditSummary | undefined;
        try {
            saved = (await preview.refetch()).data?.credit;
        } catch {
            saved = undefined;
        }
        // Si el vendedor ya pidió otra cotización, esa manda sobre la relectura.
        if (saved === undefined || ticket !== latestQuote.currentTicket()) return;
        setServer(saved);
        serverRef.current = saved;
        showCredit(saved);
    };

    // ── Commit al silo (soltar slider / blur input / click de plazo / combinación elegida).
    const commit = async (downPesos: number, weeks: number): Promise<boolean> => {
        cancelScheduledCommit();
        // Nada fuera de las cotas conocidas llega al silo: se acota antes de pedir.
        const safeDown = plan === null ? downPesos : clampDown(downPesos);
        const safeWeeks = plan === null ? weeks : nearestTerm(weeks, plan.termWeeksOptions);
        if (safeDown !== downPesos) setDown(safeDown);
        if (safeWeeks !== weeks) setTerm(safeWeeks);
        try {
            const outcome = await latestQuote.request({
                downPaymentCents: pesosToCents(safeDown),
                termWeeks: safeWeeks,
            });
            if (outcome.superseded) return false;
            applyQuoteResult(outcome.result);
            setQuoteNotice(null);
            return true;
        } catch (e) {
            learnFromRejection(e);
            if (e instanceof ApiError && e.code === 'DOWN_PAYMENT_OUT_OF_RANGE') {
                // El server manda el rango real en details (cents) — mostrarlo antes que el local.
                const d = e.data as { minCents?: number; maxCents?: number | null } | null;
                const lo = typeof d?.minCents === 'number' ? d.minCents / 100 : minDown;
                const hi = typeof d?.maxCents === 'number' ? d.maxCents / 100 : maxDown;
                setQuoteNotice(`El enganche debe estar entre ${formatMXN(lo)} y ${formatMXN(hi)}.`);
            } else if (wizardErrorCode(e) === 'RESERVATION_EXPIRED') {
                toast.error('Falta la reserva del equipo — vuelve a elegirlo para continuar.');
                void navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: 'product' },
                });
                return false;
            } else {
                // Lo que se corrige en las tarjetas va junto a ellas; lo demás, en toast.
                const failure = quoteFailure(e, priceCents);
                if (failure.adjustableHere) setQuoteNotice(failure.message);
                else toast.error(failure.message);
            }
            void resyncFromSaved();
            return false;
        }
    };

    /** Re-cotiza cuando el enganche deja de moverse: cada tecla o arrastre no es un POST. */
    const scheduleCommit = (downPesos: number, weeks: number): void => {
        cancelScheduledCommit();
        commitTimer.current = setTimeout(() => {
            commitTimer.current = null;
            void commit(downPesos, weeks);
        }, QUOTE_DEBOUNCE_MS);
    };

    /**
     * Vuelve a cotizar con los términos que el vendedor corrigió tras un rechazo del bootstrap.
     * No pasa por `commit`: sin plan los bounds valen 0 y `clampDown` aplastaría el enganche a 0.
     */
    const requote = async (): Promise<void> => {
        if (down === null || term === null) return;
        // El rescate no tiene plan, pero sí las cotas que reveló el rechazo: se respetan igual.
        const corrected = clampToRecoveryRange(down);
        if (corrected !== down) setDown(corrected);
        setRequoting(true);
        try {
            const r = await quote.mutateAsync({
                downPaymentCents: pesosToCents(corrected),
                termWeeks: term,
            });
            applyQuoteResult(r);
            setDown(centsToPesos(r.credit.downPaymentCents));
            setTerm(r.credit.termWeeks);
            setBootFailure(null);
        } catch (e) {
            learnFromRejection(e);
            setBootFailure(quoteFailure(e, priceCents));
        } finally {
            setRequoting(false);
        }
    };

    // ── Enganche y pago semanal ligados: uno fijo, el otro se calcula para el plazo.
    const contextWithTna = (tna: () => number): LinkedTermsContext | null =>
        plan === null
            ? null
            : linkedTermsContext({
                  price,
                  minDown,
                  maxDown,
                  terms: plan.termWeeksOptions,
                  tna,
                  openingFeePct: plan.openingCommissionPct,
              });
    // En los handlers manda la TNA de la última cotización; al pintar, la que ya está en estado.
    const handlerContext = (): LinkedTermsContext | null =>
        contextWithTna(() => serverRef.current?.tnaAnnual ?? plan?.tnaAnnual ?? 0);
    const renderContext = contextWithTna(() => localTna ?? plan?.tnaAnnual ?? 0);

    /** Aplica los términos resueltos y re-cotiza; el plazo va al instante, lo demás con espera. */
    const applyTerms = (
        next: { down: number; weeks: number; notice: string | null },
        immediate: boolean,
    ): void => {
        setTermsNotice(next.notice);
        setQuoteNotice(null);
        setDown(next.down);
        setTerm(next.weeks);
        const unchanged = serverFresh && next.down === down && next.weeks === term;
        if (unchanged) return;
        if (immediate) void commit(next.down, next.weeks);
        else scheduleCommit(next.down, next.weeks);
    };

    /** Mueve un control con la regla compartida del cotizador y re-cotiza. */
    const changeTerms = (change: LinkedQuoteChange): void => {
        const termsContext = handlerContext();
        if (termsContext === null || down === null || term === null) return;
        const next = applyLinkedChange(
            { fixed, payTarget, down, weeks: term },
            change,
            termsContext,
        );
        setFixed(next.fixed);
        setPayTarget(next.payTarget);
        applyTerms(next, change.kind === 'weeks');
    };

    /** «Fijar este»: el pago fijo arranca en el que se está viendo, sin re-cotizar. */
    const fixField = (field: FixedField): void => {
        setTermsNotice(null);
        const next = fixLinkedField(field, shown?.weekly ?? null);
        if (next === null) return;
        setFixed(next.fixed);
        setPayTarget(next.payTarget);
    };

    /** Relanza el bootstrap desde cero (el guard es un ref: hay que soltarlo a mano). */
    const retryBoot = (): void => {
        bootStarted.current = false;
        setBootFailure(null);
        setBootRetry((n) => n + 1);
    };

    /** El paso trabado por el cobro no re-cotiza: solo deja seguir. */
    const goToDownPayment = (): Promise<void> =>
        navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step: 'payment' } });

    const goToProduct = (): Promise<void> =>
        navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step: 'product' } });

    /** «Arma la venta»: ahí se revalida el código y se cambian las piezas que Zentral se llevó. */
    const goToBuildSale = (): Promise<void> => {
        void refresh();
        return navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'build-sale' },
        });
    };

    /** Paso 04: SureKeep, las otras financieras elegibles y el contado. */
    const goToPaymentMethod = (): Promise<void> =>
        navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'payment-method' },
        });

    const continuar = async (): Promise<void> => {
        if (down === null || term === null) return;
        setContinuing(true);
        try {
            // Asegura que el crédito quedó re-cotizado con los términos que el cliente está viendo.
            const c = clampDown(down);
            if (c !== down) setDown(c);
            if ((!serverFresh || c !== down) && !(await commit(c, term))) return;
            await navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step: 'payment' },
            });
        } finally {
            setContinuing(false);
        }
    };

    if (!session) return <Loading size="lg" />;
    const backStep = previousStepSlug('quote', session) ?? 'payment-method';
    const product = session.product;
    const hasLines = session.reservedLines !== undefined;
    const lines = chargeLinesOf(session, { credit: true, ...lineInfo });
    // Con reparto del servidor la pantalla separa lo que se paga hoy de lo que se financia.
    const hasSplit = serverSplitOf(session) !== undefined;
    const table = hasSplit ? quoteTableOf(session, lines) : null;
    const cashCents = table?.cashCents ?? 0;
    const weeklyCents = shown ? Math.round(shown.weekly * 100) : 0;
    // Total de las cuotas: `totalToPayCents` ya excluye el enganche; mientras re-cotiza, el motor local.
    const installmentsCents = serverFresh
        ? server.totalToPayCents
        : shown
          ? Math.round((shown.financed + shown.interest + shown.iva) * 100)
          : 0;
    const installmentsWithoutCents = savings ? Math.round(savings.totalWithout * 100) : 0;
    const splitNumbers: QuoteSplitNumbers | null =
        table !== null && shown !== null && plan !== null && down !== null && term !== null
            ? {
                  downPaymentCents: pesosToCents(down),
                  cashCents: table.cashCents,
                  financedCents: Math.round(shown.financed * 100),
                  openingFee:
                      plan.openingCommissionPct > 0
                          ? {
                                pct: Math.round(plan.openingCommissionPct * 10_000) / 100,
                                cents: Math.round(
                                    (priceCents - pesosToCents(down)) * plan.openingCommissionPct,
                                ),
                            }
                          : null,
                  interestCents: Math.round(shown.interest * 100),
                  ivaCents: Math.round(shown.iva * 100),
                  weeklyCents,
                  termWeeks: term,
                  installmentsCents,
                  promo:
                      savings === null
                          ? null
                          : {
                                baseTnaPct: Math.round(savings.baseTna * 100),
                                appliedTnaPct: Math.round(savings.appliedTna * 100),
                                installmentsWithoutCents,
                                weeklySavingCents: Math.round(savings.weeklySaving * 100),
                            },
              }
            : null;
    // El camino feliz con reparto: tabla por línea en lugar del desglose de cobro.
    const splitView =
        table !== null && splitNumbers !== null && !locked && !previewFailed
            ? { table, n: splitNumbers }
            : null;

    /** Enganche, semanas y cuota semanal: dos fijos y uno calculado. */
    const linkedCards =
        plan !== null && down !== null && term !== null ? (
            <LinkedQuoteCards
                fixed={fixed}
                onFix={fixField}
                down={down}
                minDown={minDown}
                maxDown={maxDown}
                rangeLabel={
                    <span className="flex justify-between gap-2">
                        {promoLoweredMinDown !== null ? (
                            <span>
                                Mín. <s className="opacity-70">{formatMXN(promoLoweredMinDown)}</s>{' '}
                                <span className="font-bold text-promo">{formatMXN(minDown)}</span>
                            </span>
                        ) : (
                            <span>Mín. {formatMXN(minDown)}</span>
                        )}
                        <span>Máx. {formatMXN(maxDown)}</span>
                    </span>
                }
                downShare={`${String(Math.round((down / price) * 100))}% ${headline ? 'del equipo + extras a crédito' : 'del precio'}`}
                onDownChange={setDown}
                onDownCommit={(pesos) => {
                    changeTerms({ kind: 'down', down: pesos });
                }}
                weeks={term}
                terms={plan.termWeeksOptions}
                onWeeksCommit={(weeks) => {
                    changeTerms({ kind: 'weeks', weeks });
                }}
                pay={shown?.weekly ?? null}
                payTarget={fixed === 'pay' ? payTarget : null}
                payRange={renderContext === null ? null : paymentRange(term, renderContext)}
                onPayCommit={(pay) => {
                    changeTerms({ kind: 'pay', pay });
                }}
                payTerms={`${String(term)} cuotas fijas · tasa anual ${String(Math.round((shown?.tna ?? plan.tnaAnnual) * 100))} %`}
                notice={quoteNotice ?? termsNotice}
            />
        ) : null;

    // Fuera de la pista de crédito (sin nivel) no hay nada que cotizar.
    if (!level) {
        return (
            <WizardCard>
                <WizardCardHead eyebrow="Crédito SureKeep" title="Cotización" />
                <WizardCardBody>
                    <p className="text-[12.5px] text-muted-foreground">
                        Elige SureKeep en Forma de pago.
                    </p>
                </WizardCardBody>
                <WizardFootNav back={<WizardBackLink sessionId={sessionId} step={backStep} />} />
            </WizardCard>
        );
    }

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Crédito SureKeep"
                title="Cotización"
                right={<LevelChip level={level} />}
                sub={
                    unquotable ? undefined : headline && splitView ? undefined : headline ? (
                        <span data-testid="quote-headline">
                            Equipo neto{' '}
                            <span className="tabular-nums">
                                {formatMXN(headline.anchorCents / 100)}
                            </span>{' '}
                            + extras a crédito{' '}
                            <span className="tabular-nums">
                                {formatMXN(headline.extrasCents / 100)}
                            </span>{' '}
                            ={' '}
                            <strong className="font-semibold tabular-nums text-foreground">
                                {formatMXN(headline.financedCents / 100)}
                            </strong>
                        </span>
                    ) : hasLines ? (
                        financedLine ? (
                            <>
                                Se financia:{' '}
                                <strong className="font-semibold text-foreground">
                                    {lines.find((l) => l.financed)?.name}
                                </strong>{' '}
                                · Precio fijado:{' '}
                                <span className="tabular-nums">{formatMXN(price)}</span>
                            </>
                        ) : undefined
                    ) : product ? (
                        <>
                            Equipo:{' '}
                            <strong className="font-semibold text-foreground">
                                {catalogDisplayName(product, lineInfo.descriptions)}
                            </strong>{' '}
                            · Precio: <span className="tabular-nums">{formatMXN(price)}</span>
                        </>
                    ) : undefined
                }
            />

            <WizardCardBody className="flex flex-col gap-5">
                {!splitView && hasLines && lines.length > 0 && (
                    <ChargeBreakdown
                        lines={lines}
                        slots={slots}
                        discounts={lineDiscountsOf(session, lines)}
                        splitAmounts={hasSplit}
                        totalLabel="Total de la venta"
                        totalCents={linesTotalCents(lines)}
                    />
                )}
                {locked ? (
                    /* ── Enganche ya cobrado: términos en solo lectura, sin nada que editar ── */
                    <div className="flex flex-col gap-4">
                        <div className="rounded-lg border border-border bg-card p-4">
                            <p className="text-[13px] font-semibold">
                                El enganche de esta venta ya se cobró
                            </p>
                            {/* En crédito el motor originó sobre ESTE equipo: ni recotizar, ni
                                cancelar, ni cambiar el equipo. La única salida es terminar. */}
                            <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
                                La cotización ya no cambia.
                            </p>
                        </div>
                        {agreed ? (
                            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                {[
                                    {
                                        label: 'Enganche',
                                        value: formatMXN(agreed.downPaymentCents / 100),
                                    },
                                    {
                                        label: 'Plazo',
                                        value: `${String(agreed.termWeeks)} semanas`,
                                    },
                                    {
                                        label: 'Cuota semanal',
                                        value: formatMXN(agreed.weeklyPaymentWithIvaCents / 100),
                                    },
                                ].map((item) => (
                                    <div
                                        key={item.label}
                                        className="rounded-lg border border-border bg-card p-3"
                                    >
                                        <dt className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                                            {item.label}
                                        </dt>
                                        <dd className="mt-1 text-[16px] font-semibold tabular-nums">
                                            {item.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        ) : (
                            <p className="text-[12px] text-muted-foreground">
                                No se pudieron leer los términos del crédito. El cobro ya está
                                registrado: continúa al enganche para verlo ahí.
                            </p>
                        )}
                    </div>
                ) : previewFailed ? (
                    /* Leer de nuevo es barato; cotizar reescribiría el acuerdo, así que no se hace. */
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {wizardErrorMsg(
                                preview.error,
                                'No se pudo leer la cotización que ya tenía esta venta.',
                            )}
                        </p>
                        <Button
                            size="sm"
                            isLoading={preview.isFetching}
                            onClick={() => void preview.refetch()}
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : unquotable ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {productNotFinanceableFailure().message}
                        </p>
                        <QuoteFailureActions
                            failure={productNotFinanceableFailure()}
                            onRetry={retryBoot}
                            onProduct={() => void goToProduct()}
                            onPaymentMethod={() => void goToPaymentMethod()}
                            onBuildSale={() => void goToBuildSale()}
                        />
                    </div>
                ) : !plan || down === null || term === null ? (
                    bootFailure === null ? (
                        <div className="flex flex-col items-center gap-2 py-10 text-center">
                            <Loader2
                                className="size-6 animate-spin text-muted-foreground"
                                aria-hidden
                            />
                            <p className="text-[12.5px] text-muted-foreground">
                                Armando la cotización con el plan del nivel…
                            </p>
                        </div>
                    ) : sinSalida ? (
                        /* Contradicción entre las cotas: no existe enganche válido. Se explica y se
                           sale a las alternativas — pedir que muevan la barra sería mandarlos a un
                           callejón sin salida con forma de formulario. */
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <p className="text-[12.5px] text-muted-foreground">
                                {noFeasibleDownPaymentMessage(priceCents, capFinanciable)}
                            </p>
                            <QuoteFailureActions
                                failure={bootFailure}
                                onRetry={retryBoot}
                                onProduct={() => void goToProduct()}
                                onPaymentMethod={() => void goToPaymentMethod()}
                                onBuildSale={() => void goToBuildSale()}
                            />
                        </div>
                    ) : bootFailure.adjustableHere && down !== null && term !== null ? (
                        /* Rechazo de parámetro: el enganche y el plazo se quedan en pantalla. */
                        <QuoteRecoveryEditor
                            failure={bootFailure}
                            price={price}
                            minDownPayment={recoveryRange.min}
                            maxDownPayment={recoveryRange.max}
                            /* Acotado al pintar y no por estado: el arranque fija el monto DESPUÉS
                               de aprender el piso, y sincronizarlo por efecto se pisaba solo. */
                            downPayment={clampToRecoveryRange(down)}
                            onDownPaymentChange={setDown}
                            termWeeks={term}
                            onTermWeeksChange={setTerm}
                            isQuoting={requoting}
                            onRequote={() => void requote()}
                            actions={
                                <QuoteFailureActions
                                    failure={bootFailure}
                                    onRetry={retryBoot}
                                    onProduct={() => void goToProduct()}
                                    onPaymentMethod={() => void goToPaymentMethod()}
                                    onBuildSale={() => void goToBuildSale()}
                                    variant="outline"
                                />
                            }
                        />
                    ) : (
                        /* Sin arreglo en esta pantalla: no hay control que mover y el formulario
                           solo estorbaría. El botón sale de la ACCIÓN que resuelve el fallo. */
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <p className="text-[12.5px] text-muted-foreground">
                                {bootFailure.message}
                            </p>
                            <QuoteFailureActions
                                failure={bootFailure}
                                onRetry={retryBoot}
                                onProduct={() => void goToProduct()}
                                onPaymentMethod={() => void goToPaymentMethod()}
                                onBuildSale={() => void goToBuildSale()}
                            />
                        </div>
                    )
                ) : (
                    <>
                        <section
                            aria-labelledby="quote-adjust-title"
                            className="flex flex-col gap-2.5"
                        >
                            <h2 id="quote-adjust-title" className="text-[13.5px] font-bold">
                                Ajusta el crédito
                            </h2>
                            {linkedCards}
                        </section>

                        {splitView && (
                            <>
                                <hr className="border-border" />
                                <section
                                    aria-labelledby="quote-split-title"
                                    className="flex flex-col gap-2.5"
                                >
                                    <h2 id="quote-split-title" className="text-[13.5px] font-bold">
                                        Cómo se paga
                                    </h2>
                                    <QuoteSaleTotals
                                        table={splitView.table}
                                        downPaymentCents={splitView.n.downPaymentCents}
                                        financedCents={splitView.n.financedCents}
                                        slots={slots}
                                    />
                                    {splitView.table.capFilled && (
                                        <p
                                            data-testid="quote-extras-cap"
                                            className="text-[11.5px] text-muted-foreground"
                                        >
                                            Se llenó el tope del 30 % del equipo: lo que no cupo va
                                            de contado
                                        </p>
                                    )}
                                </section>
                            </>
                        )}

                        {splitNumbers !== null && (
                            <>
                                <hr className="border-border" />
                                <section
                                    aria-labelledby="quote-summary-title"
                                    className="flex flex-col gap-2.5"
                                >
                                    <h2
                                        id="quote-summary-title"
                                        className="text-[13.5px] font-bold"
                                    >
                                        Resumen del crédito
                                    </h2>
                                    <QuoteSplitSummary n={splitNumbers} />
                                </section>
                            </>
                        )}

                        {/* ── Desglose ── */}
                        {!hasSplit && shown && totalCliente !== null && (
                            <CreditBreakdown
                                numbers={{
                                    price,
                                    down,
                                    financed: shown.financed,
                                    interest: shown.interest,
                                    iva: shown.iva,
                                    tna: shown.tna,
                                    weekly: shown.weekly,
                                    weeks: term,
                                    total: totalCliente,
                                }}
                                priceLabel={
                                    headline ? 'Equipo + extras a crédito' : 'Precio a financiar'
                                }
                                savings={savings}
                                bonusCents={bonusCents}
                                cashCents={cashCents}
                            />
                        )}
                    </>
                )}
            </WizardCardBody>

            <WizardFootNav back={<WizardBackLink sessionId={sessionId} step={backStep} />}>
                {locked ? (
                    <Button
                        size="sm"
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void goToDownPayment()}
                    >
                        Continuar al enganche
                    </Button>
                ) : plan && down !== null && term !== null ? (
                    <Button
                        size="sm"
                        isLoading={continuing}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void continuar()}
                    >
                        {hasSplit ? 'Continuar al cobro' : 'Continuar al enganche'}
                    </Button>
                ) : null}
            </WizardFootNav>
        </WizardCard>
    );
}

/**
 * Las salidas de un rechazo: la principal como botón y la segunda en enlace bajo ella.
 * Reintentar solo aparece cuando el fallo fue del transporte, nunca ante un rechazo del plan.
 */
function QuoteFailureActions({
    failure,
    onRetry,
    onProduct,
    onPaymentMethod,
    onBuildSale,
    variant,
}: {
    failure: QuoteFailure;
    onRetry: () => void;
    onProduct: () => void;
    onPaymentMethod: () => void;
    onBuildSale: () => void;
    /** `outline` donde la pantalla ya tiene su acción principal (el editor de rescate). */
    variant?: 'outline';
}) {
    return (
        <>
            {failure.recovery === 'retry' ? (
                <Button size="sm" variant={variant} onClick={onRetry}>
                    {failure.actionLabel ?? 'Reintentar'}
                </Button>
            ) : null}
            {failure.recovery === 'product' ? (
                <Button size="sm" variant={variant} onClick={onProduct}>
                    Elegir otro equipo
                </Button>
            ) : null}
            {failure.recovery === 'build-sale' ? (
                <Button size="sm" variant={variant} onClick={onBuildSale}>
                    {failure.actionLabel ?? 'Revisar el código'}
                </Button>
            ) : null}
            {failure.recovery === 'payment-method' ? (
                <Button size="sm" variant={variant} onClick={onPaymentMethod}>
                    {failure.actionLabel ?? 'Ver otras formas de pago'}
                </Button>
            ) : null}
            {failure.secondary === 'payment-method' ? (
                <Button size="sm" variant="link" onClick={onPaymentMethod}>
                    Ver otras formas de pago
                </Button>
            ) : null}
        </>
    );
}

/**
 * Rechazo que el vendedor corrige aquí: el aviso va ARRIBA del control, no en lugar del formulario.
 * Sin plan el slider viaja entre el paso mínimo y el precio menos $100 — el silo valida el monto.
 */
function QuoteRecoveryEditor({
    failure,
    price,
    minDownPayment,
    maxDownPayment,
    downPayment,
    onDownPaymentChange,
    termWeeks,
    onTermWeeksChange,
    isQuoting,
    onRequote,
    actions,
}: {
    failure: QuoteFailure;
    price: number;
    /** Cotas efectivas que reveló el rechazo; `null` = no se conocen y el silo valida el monto. */
    minDownPayment: number | null;
    maxDownPayment: number | null;
    downPayment: number;
    onDownPaymentChange: (value: number) => void;
    termWeeks: number;
    onTermWeeksChange: (value: number) => void;
    isQuoting: boolean;
    onRequote: () => void;
    actions: React.ReactNode;
}) {
    const floor = minDownPayment ?? STEP;
    const ceiling = Math.max(floor, Math.min(maxDownPayment ?? Infinity, Math.floor(price - 100)));
    const terms = failure.availableTermWeeks ?? [];

    return (
        <div className="flex flex-col gap-5">
            <div
                role="alert"
                className="rounded-[10px] border border-warning/40 bg-warning/10 px-4 py-3 text-[12.5px] leading-relaxed"
            >
                {failure.message}
            </div>

            <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                    <label
                        className="flex-none text-[13px] font-semibold"
                        htmlFor="quote-recovery-down"
                    >
                        Enganche
                    </label>
                    <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-muted-foreground">$</span>
                        <NumberInput
                            id="quote-recovery-down"
                            value={downPayment}
                            min={floor}
                            max={ceiling}
                            step={0.01}
                            aria-label="Enganche"
                            className="h-[34px] w-[120px] text-right text-[16px] font-bold"
                            onValueChange={onDownPaymentChange}
                        />
                        <div className="text-[11px] tabular-nums text-muted-foreground">
                            {Math.round((downPayment / price) * 100)}% del precio
                        </div>
                    </div>
                </div>
                {/* Con el mínimo real ya conocido el control lo anuncia; sin él lo valida la cotización. */}
                <p className="mb-2 text-[11px] text-muted-foreground">
                    {minDownPayment === null
                        ? 'Muévelo y vuelve a cotizar: el plan del cliente valida el monto.'
                        : `Muévelo y vuelve a cotizar: este cliente no puede dar menos de ${formatMXN(minDownPayment)}.`}
                </p>
                <input
                    type="range"
                    min={floor}
                    max={ceiling}
                    step={SLIDER_STEP}
                    value={downPayment}
                    aria-label="Ajustar enganche"
                    onChange={(e) => {
                        onDownPaymentChange(Number(e.target.value));
                    }}
                    className="w-full accent-primary"
                />
                <div className="mt-1 flex justify-between text-[10.5px] tabular-nums text-muted-foreground">
                    <span>
                        {minDownPayment === null ? formatMXN(floor) : `Mín. ${formatMXN(floor)}`}
                    </span>
                    <span>
                        {maxDownPayment === null
                            ? formatMXN(ceiling)
                            : `Máx. ${formatMXN(ceiling)}`}
                    </span>
                </div>
            </div>

            {terms.length > 0 ? (
                <div>
                    <div className="mb-2 block text-[13px] font-semibold">Plazo semanal</div>
                    <div className="flex flex-wrap gap-2">
                        {terms.map((weeks) => (
                            <button
                                key={weeks}
                                type="button"
                                aria-pressed={weeks === termWeeks}
                                onClick={() => {
                                    onTermWeeksChange(weeks);
                                }}
                                className={cn(
                                    'rounded-lg border px-5 py-2.5 text-[13px] font-semibold transition-colors',
                                    weeks === termWeeks
                                        ? 'border-foreground bg-muted/60 text-foreground'
                                        : 'border-border bg-card text-muted-foreground hover:border-primary/50',
                                )}
                            >
                                {weeks} sem
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" isLoading={isQuoting} onClick={onRequote}>
                    Cotizar de nuevo
                </Button>
                {actions}
            </div>
        </div>
    );
}
